// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { programsMiddleware } from '@makemydeal/dr-dash-store/middleware/programsMiddleware';
import { middleware as leaseEndpointMiddleware } from '@makemydeal/dr-dash-store/middleware/leaseEndpointMiddleware';
import { middleware as programInvalidationMiddleware } from '@makemydeal/dr-dash-store/middleware/programInvalidationMiddleware';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

/** Chains middlewares outermost-first, instantiating each once per store rather than once per action. */
const composeMiddleware =
    (...factories: ReduxMiddleware<StateTree>[]) =>
    (store: ReduxStore<StateTree>) => {
        const links = factories.map((factory) => factory(store));
        return (next: ReduxNext) =>
            links.reduceRight<ReduxNext>((inner, link) => link(inner) as ReduxNext, next) as (action: AnyFSA) => void;
    };

/**
 * `programsMiddleware` outermost, the two endpoint middlewares innermost — but the position is not
 * load-bearing, so don't preserve it on faith. `next(action)` reaches the reducers before any
 * middleware's post-`next` code at every position, so "reducers have settled" is true anywhere.
 * What position decides is ordering against the siblings' post-`next` work: `programsMiddleware`
 * being outermost means its own post-`next` block runs after these two, so they cannot see what it
 * dispatches. That's fine only because they read program state from the offer/terms reducers, never
 * from `programsSelectors.getHasSelectedProgram`, which that block writes.
 *
 * The endpoint middlewares are mutually exclusive by feature toggle — with
 * `F283308_Enable_Improved_Auto_Select` on, `leaseEndpointMiddleware` passes everything straight
 * through — so their order relative to each other carries no behaviour either.
 *
 * These three used to be registered through `programsConfiguration`, whose only remaining job was
 * to carry them; the /programs route it configured is retired. They drive program selection and
 * payment, not the retired page, so they keep their exact position in `getRemainingMiddleware`.
 */
export const programsWithEndpointMiddleware = composeMiddleware(
    programsMiddleware,
    programInvalidationMiddleware,
    leaseEndpointMiddleware
);
